import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import JobMap, { useGeocodedJobs } from "@/components/JobMap";
import JobWeather from "@/components/JobWeather";
import JobAssessment from "@/components/JobAssessment";
import JobAttachments from "@/components/JobAttachments";
import JobTasks from "@/components/JobTasks";
import WeatherWidget from "@/components/WeatherWidget";
import { MetarPanel } from "@/components/MetarPanel";
import SwmsDialog from "@/components/SwmsDialog";
import { toast } from "sonner";
import { STATUS_STYLES, JOB_TYPE_LABELS, fmtTime, fmtDate } from "@/lib/helpers";
import { ArrowLeft, Pencil, MapPin, Plane, User, Users, Clock, Mail, Boxes, Loader2, Check, Building2, Phone, LifeBuoy, ShieldAlert, FileText, StickyNote } from "lucide-react";

export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetGroups, setAssetGroups] = useState([]);
  const [clients, setClients] = useState([]);
  const [emails, setEmails] = useState([]);
  const [sar, setSar] = useState({ name: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(true);
  const [swmsOpen, setSwmsOpen] = useState(false);
  const [jobNotes, setJobNotes] = useState([]);

  const load = useCallback(async () => {
    try {
      const [j, e, a, ag, cl, em, s, nt] = await Promise.all([
        api.get(`/jobs/${id}`), api.get("/employees"), api.get("/assets"), api.get("/asset-groups"),
        api.get("/clients"), api.get(`/jobs/${id}/emails`), api.get("/settings/sar"),
        api.get(`/notes/by-job/${id}`),
      ]);
      setJob(j.data); setEmployees(e.data); setAssets(a.data); setAssetGroups(ag.data);
      setClients(cl.data); setEmails(em.data); setSar(s.data); setJobNotes(nt.data);
    } catch { setJob(null); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("jobs:refresh", h);
    return () => window.removeEventListener("jobs:refresh", h);
  }, [load]);

  const points = useGeocodedJobs(job && job.location ? [{ id: job.id, title: job.title, location: job.location, time: fmtTime(job.start) }] : []);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-lime-500" /></div>;
  if (!job) return <div className="text-center py-24 text-slate-500">Job not found. <button className="text-lime-600 underline" onClick={() => navigate("/calendar")}>Back to calendar</button></div>;

  const pic = employees.find((e) => e.id === job.employee_id);
  const assistants = (job.assistant_ids || []).map((aid) => employees.find((e) => e.id === aid)).filter(Boolean);
  const assignedAssets = (job.asset_ids && job.asset_ids.length ? job.asset_ids : (job.asset_id ? [job.asset_id] : []))
    .map((aid) => assets.find((a) => a.id === aid)).filter(Boolean);
  const assignedGroups = (job.asset_group_ids || []).map((gid) => assetGroups.find((g) => g.id === gid)).filter(Boolean);
  const client = clients.find((c) => c.id === job.client_id);
  const st = STATUS_STYLES[job.status] || STATUS_STYLES.scheduled;

  const sarName = job.sar_name || sar.name;
  const sarPhone = job.sar_phone || sar.phone;
  const sarNotes = job.sar_notes || sar.notes;
  const hasSar = sarName || sarPhone || sarNotes;

  const Field = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
      <div><p className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</p><p className="text-sm text-slate-800 mt-0.5">{value || "—"}</p></div>
    </div>
  );

  return (
    <div className="space-y-6 fade-up">
      <button onClick={() => navigate(-1)} data-testid="back-btn" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">{job.title}</h1>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${st.bg} ${st.text} ${st.border}`}>{st.label}</span>
          </div>
          <p className="text-slate-500 mt-1">{client?.name || job.client_name} · {JOB_TYPE_LABELS[job.job_type] || job.job_type}</p>
        </div>
        <div className="flex items-center gap-2 self-start">
          <Button data-testid="job-sheet-pdf-btn" variant="outline" onClick={() => {
            const token = localStorage.getItem("session_token");
            window.open(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${job.id}/job-sheet/pdf?auth=${encodeURIComponent(token || "")}`, "_blank");
          }}>
            <FileText className="h-4 w-4 mr-1.5" /> Create PDF
          </Button>
          <Button data-testid="edit-job-btn" onClick={() => navigate(`/jobs/${job.id}/edit`)} className="bg-slate-900 hover:bg-slate-800 text-white">
            <Pencil className="h-4 w-4 mr-1.5" /> Edit Job
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 space-y-6 min-w-0">
          <JobMap points={points} height="380px" />
          <JobWeather location={job.location} dateISO={job.start} />

          <div className="bg-white border border-slate-200 rounded-xl p-6 grid sm:grid-cols-2 gap-5">
            <Field icon={Clock} label="Date & Time" value={`${fmtDate(job.start)} · ${fmtTime(job.start)}${job.end ? ` – ${fmtTime(job.end)}` : ""}`} />
            <Field icon={MapPin} label="Location" value={job.location} />
            <Field icon={User} label="Pilot in Command" value={pic?.name} />
            <Field icon={Plane} label="Drone Type" value={job.drone_type} />
            <Field icon={Check} label="AVCRM" value={job.avcrm_loaded ? "Loaded" : "Not yet loaded"} />
          </div>

          {/* Crew */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-3"><Users className="h-4 w-4" /> Crew</h2>
            <div className="flex flex-wrap gap-2">
              {pic && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-lime-100 text-lime-800 text-sm px-3 py-1" data-testid="crew-pic">
                  <span className="h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: pic.color || "#84cc16", color: "#0f172a" }}>{pic.name.charAt(0)}</span>
                  {pic.name} <span className="text-[10px] font-bold uppercase text-lime-600">PIC</span>
                </span>
              )}
              {assistants.map((a) => (
                <span key={a.id} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 text-sm px-3 py-1" data-testid={`crew-assistant-${a.id}`}>
                  <span className="h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: a.color || "#e2e8f0", color: "#0f172a" }}>{a.name.charAt(0)}</span>
                  {a.name}
                </span>
              ))}
              {!pic && assistants.length === 0 && <p className="text-sm text-slate-400">No crew assigned.</p>}
            </div>
          </div>

          {/* Assigned assets & groups */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-3"><Boxes className="h-4 w-4" /> Assigned Assets</h2>
            {assignedAssets.length === 0 && assignedGroups.length === 0 ? (
              <p className="text-sm text-slate-400">No assets or groups assigned.</p>
            ) : (
              <div className="space-y-3">
                {assignedGroups.map((g) => {
                  const members = assets.filter((a) => a.group_id === g.id);
                  return (
                    <div key={g.id} data-testid={`job-group-${g.id}`} className="border border-violet-200 rounded-lg overflow-hidden">
                      <div className="flex items-center gap-1.5 bg-violet-50 text-violet-800 px-3 py-2 text-sm font-medium">
                        <Boxes className="h-4 w-4" /> {g.name} <span className="text-[10px] uppercase font-bold">group</span>
                        <span className="ml-auto text-xs text-violet-500">{members.length} asset{members.length !== 1 ? "s" : ""}</span>
                      </div>
                      {members.length === 0 ? (
                        <p className="text-sm text-slate-400 px-3 py-2">No assets in this group.</p>
                      ) : (
                        <ul className="divide-y divide-slate-100">
                          {members.map((a) => (
                            <li key={a.id}>
                              <button onClick={() => navigate(`/assets/${a.id}`)} data-testid={`job-group-asset-${a.id}`}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left">
                                <Plane className="h-3.5 w-3.5 text-lime-600 shrink-0" />
                                <span className="font-medium">{a.name}</span>
                                <span className="text-xs text-slate-400">{a.registration || a.model}</span>
                                <span className="ml-auto text-[10px] uppercase text-slate-400">{a.category}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
                {assignedAssets.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {assignedAssets.map((a) => (
                      <button key={a.id} onClick={() => navigate(`/assets/${a.id}`)} data-testid={`job-asset-${a.id}`}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-800 text-sm px-3 py-1.5 hover:bg-slate-100">
                        <Plane className="h-3.5 w-3.5 text-lime-600" /> {a.name} <span className="text-xs text-slate-400">{a.registration || a.model}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {job.notes && (
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Notes</p>
              <p className="text-sm text-slate-700 whitespace-pre-line">{job.notes}</p>
            </div>
          )}

          <JobTasks
            jobId={job.id}
            jobTitle={job.title}
            employees={employees}
            tasks={job.tasks || []}
            center={points.length ? [points[0].lat, points[0].lon] : null}
            onChange={(tasks) => setJob((j) => ({ ...j, tasks }))}
          />

          <JobAssessment jobId={job.id} kind="risk" />

          {/* Related emails — under risk assessment */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-3"><Mail className="h-4 w-4" /> Related Emails ({emails.length})</h2>
            {emails.length === 0 ? (
              <p className="text-sm text-slate-400">No emails linked. Link them from the Emails page.</p>
            ) : (
              <div className="space-y-2">
                {emails.map((em) => (
                  <div key={em.id} className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                    <p className="text-sm font-medium text-slate-800 truncate">{em.subject}</p>
                    <p className="text-xs text-slate-500 truncate">{em.from_name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4 min-w-0">
          {/* Notes tagged to this job */}
          {jobNotes.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="job-notes-card">
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-3"><StickyNote className="h-4 w-4 text-amber-500" /> Notes</h2>
              <div className="space-y-2">
                {jobNotes.map((n) => (
                  <div key={n.id} data-testid={`job-note-${n.id}`} className="border border-amber-200 rounded-lg p-3 bg-amber-100 shadow-sm">
                    <p className="text-sm text-amber-950 whitespace-pre-line">{n.text}</p>
                    <p className="text-xs text-amber-700/70 mt-2">{n.created_by_name || "—"} · {fmtDate(n.created_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Client details */}
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="job-client-card">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-3"><Building2 className="h-4 w-4" /> Client</h2>
            {client ? (
              <div className="space-y-1.5 text-sm text-slate-700">
                <p className="font-heading font-semibold text-slate-900">{client.name}</p>
                {client.contact_name && <p className="flex items-center gap-2"><User className="h-4 w-4 text-slate-400" />{client.contact_name}</p>}
                {client.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /><a href={`tel:${client.phone}`} className="hover:text-lime-600">{client.phone}</a></p>}
                {client.email && <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /><a href={`mailto:${client.email}`} className="hover:text-lime-600 truncate">{client.email}</a></p>}
                {client.address && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /><span className="truncate">{client.address}</span></p>}
              </div>
            ) : (
              <p className="text-sm text-slate-400">{job.client_name || "No client linked."} <button onClick={() => navigate(`/jobs/${job.id}/edit`)} className="text-lime-600 underline">Link one</button></p>
            )}
          </div>

          {/* SAR / emergency contact */}
          <div className="bg-white border border-orange-200 rounded-xl p-5" data-testid="job-sar-card">
            <h2 className="text-sm font-bold uppercase tracking-wider text-orange-600 flex items-center gap-1.5 mb-3"><LifeBuoy className="h-4 w-4" /> SAR / Emergency</h2>
            {hasSar ? (
              <div className="space-y-1.5 text-sm text-slate-700">
                {sarName && <p className="flex items-center gap-2"><User className="h-4 w-4 text-slate-400" />{sarName}</p>}
                {sarPhone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-orange-500" /><a href={`tel:${sarPhone}`} className="font-semibold text-slate-900 hover:text-orange-600">{sarPhone}</a></p>}
                {sarNotes && <p className="text-xs text-slate-500">{sarNotes}</p>}
                {!job.sar_name && !job.sar_phone && !job.sar_notes && <p className="text-[11px] text-slate-400 pt-1">Global default (set in Settings)</p>}
              </div>
            ) : (
              <p className="text-sm text-slate-400">No SAR contact set. Add one on this job or set a global default in Settings.</p>
            )}
          </div>

          {/* Weather */}
          <WeatherWidget initialLocation={job.location} forecastDate={job.start} />

          {/* Aviation weather — METAR / TAF */}
          <JobMetarCard job={job} coords={points[0]} onSaved={(j) => setJob(j)} />

          {/* Safety document (SWMS) */}
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="job-swms-card">
            <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-1"><ShieldAlert className="h-5 w-5 text-lime-500" /> Safety Document (SWMS)</h3>
            <p className="text-sm text-slate-500">Complete the drone SWMS for this job — pre-filled from job details — and download a branded PDF for staff & third parties.</p>
            <Button onClick={() => setSwmsOpen(true)} data-testid="open-job-swms-btn" className="mt-3 w-full bg-slate-900 hover:bg-slate-800 text-white"><FileText className="h-4 w-4 mr-1.5" /> Open SWMS</Button>
          </div>

          {/* Attachments */}
          <JobAttachments jobId={job.id} attachments={job.attachments || []} onChange={(atts) => setJob((j) => ({ ...j, attachments: atts }))} />

          {/* DAMP checklist — where emails previously were */}
          <JobAssessment jobId={job.id} kind="damp" />
        </div>
      </div>

      <SwmsDialog open={swmsOpen} onOpenChange={setSwmsOpen} mode="job" jobId={job.id} />
    </div>
  );
}

function JobMetarCard({ job, coords, onSaved }) {
  const [icao, setIcao] = useState(job.metar_icao || "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const manual = (job.metar_icao || "").trim();

  const save = async () => {
    setSaving(true);
    try {
      const v = icao.trim().toUpperCase();
      const r = await api.put(`/jobs/${job.id}`, { ...job, metar_icao: v });
      onSaved(r.data);
      setEditing(false);
      toast.success(v ? `Station set to ${v}` : "Using nearest aerodrome");
    } catch { toast.error("Could not save station"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-2" data-testid="job-metar">
      <MetarPanel
        icao={manual || undefined}
        lat={manual ? undefined : coords?.lat}
        lon={manual ? undefined : coords?.lon}
        title="Aviation Weather"
      />
      <div className="flex items-center gap-2">
        {editing ? (
          <>
            <input value={icao} onChange={(e) => setIcao(e.target.value.toUpperCase())} placeholder="ICAO e.g. YHBA"
              data-testid="job-metar-icao-input" className="flex-1 h-8 rounded-md border border-slate-200 px-2 text-sm font-mono" />
            <Button size="sm" onClick={save} disabled={saving} data-testid="job-metar-save" className="h-8 bg-slate-900 hover:bg-slate-800 text-white">{saving ? "…" : "Save"}</Button>
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setIcao(job.metar_icao || ""); }} className="h-8">Cancel</Button>
          </>
        ) : (
          <button onClick={() => setEditing(true)} data-testid="job-metar-edit" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            <Pencil className="h-3 w-3" /> {manual ? `Station: ${manual} (override)` : "Set station manually"}
          </button>
        )}
      </div>
    </div>
  );
}
